import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { ChartOrderItem, DataType } from '../../utils/types/analytics/analytics_types';
import { LightColors, MediumColors } from './Colors';
import { DashletIconGroup } from './DashletIconGroup';

const colors = [
  MediumColors.RED, MediumColors.BLUE, MediumColors.ORANGE, MediumColors.GREEN, MediumColors.FUCHSIA,
  MediumColors.PURPLE, MediumColors.CYAN, MediumColors.PINK, MediumColors.YELLOW, MediumColors.LIME,
  LightColors.GREEN, LightColors.BLUE, LightColors.ORANGE, LightColors.RED, LightColors.FUCHSIA,
  LightColors.PURPLE, LightColors.CYAN, LightColors.PINK, LightColors.YELLOW, LightColors.LIME,
];
const RADIAN = Math.PI / 180;

type PieChartDashletProps = {
  id: string;
  data: Array<DataType>;
  title: string;
  dashletItem: ChartOrderItem;
  onDragStart: (item: ChartOrderItem) => void;
  onDragEnter: (item: ChartOrderItem) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  fullScreenDetected: boolean;
  setFullScreenDetected: (fullscreen: boolean) => void;
  handleDashletVisibility: (item: ChartOrderItem) => void;
  editMode: boolean;
};

type RenderCustomizedLabelProps = {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  value: number;
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }: RenderCustomizedLabelProps) => {
  const radius = innerRadius + (outerRadius - innerRadius) * (midAngle > 340 ? 0.8 : 0.6);
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="black"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      style={{
        fontSize: outerRadius === 200 ? '20px' : '14px',
        fontWeight: 600,
      }}
    >
      {value}
    </text>
  );
};

export const PieChartDashlet = ({
  id,
  data,
  title,
  dashletItem,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  fullScreenDetected,
  setFullScreenDetected,
  handleDashletVisibility,
  editMode,
}: PieChartDashletProps) => {
  const handleDragStart = () => {
    if (onDragStart) {
      onDragStart(dashletItem);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(dashletItem);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event);
    }
  };

  const CustomTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: payload[0].payload?.fill || 'text.primary' }}
          >
            {`${payload[0].name} : ${payload[0].value}`}
          </Typography>
        </Paper>
      );
    }

    return null;
  };

  return (
    <Paper
      variant="outlined"
      className="group"
      sx={{
        position: 'relative',
        width: '100%',
        gridColumn: { xs: 'span 1', lg: 'span 2' },
        borderRadius: '16px',
        height: '100%',
        p: 2.5,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
          borderColor: 'primary.main',
        },
      }}
    >
      <DashletIconGroup
        id={id}
        dashletItem={dashletItem}
        setFullScreenDetected={setFullScreenDetected}
        handleDashletVisibility={handleDashletVisibility}
        editMode={editMode}
      />

      <Box
        id={id}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          width: '100%',
          height: '100%',
          opacity: dashletItem.hidden ? 0.4 : 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: fullScreenDetected ? '1.5rem' : '0.875rem',
            color: 'text.secondary',
            mb: 2,
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          {!data || !data.length ? (
            <Box
              sx={{
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              No data to preview
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={fullScreenDetected ? 500 : 300 + data.length * 3}>
              <PieChart>
                <Legend
                  iconSize={fullScreenDetected ? 20 : 12}
                  wrapperStyle={{ fontSize: fullScreenDetected ? '20px' : '12px' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={fullScreenDetected ? 200 : 100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
